import React from "react";
import { FileText, Upload } from "lucide-react";

function humanFileSize(bytes) {
  if (!bytes || typeof bytes !== "number") return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

function fileTypeLabel(doc) {
  // Prefer explicit labels from backend / VM
  if (doc.typeLabel) return doc.typeLabel;
  if (doc.recordType === "lab") return "Analiza laboratorike";
  if (doc.recordType === "epicrisis") return "Epikrizë";
  if (doc.recordType === "rx") return "Radiografi / Imaging";
  if (doc.recordType === "prescription") return "Recetë mjekësore";

  // Try infer from contentType / extension
  const ct = doc.contentType || doc.fileType || "";
  const name = doc.fileName || doc.title || "";
  if (ct.includes("pdf") || name.toLowerCase().endsWith(".pdf")) return "PDF";
  if (ct.includes("image/")) return "Imazh";
  return "Dokument";
}

function fileExtension(doc) {
  if (doc.extension) return doc.extension.toUpperCase();
  const name = doc.fileName || doc.title || "";
  const idx = name.lastIndexOf(".");
  if (idx === -1) return "";
  return name.slice(idx + 1).toUpperCase();
}

export default function MyHealthDocuments({
  documents = [],
  onUpload,
  onOpenDocument, // (doc) => void
}) {
  return (
    <section className="p2-myhealth-docs">
      <div className="p2-myhealth-docs-header">
        <p className="p2-myhealth-docs-title">Dokumentet mjekësore</p>
        <button
          type="button"
          onClick={onUpload}
          className="p2-myhealth-docs-upload"
        >
          <Upload className="p2-myhealth-docs-upload-icon" />
          Ngarko
        </button>
      </div>

      {documents.length === 0 ? (
        <p className="p2-myhealth-docs-empty">
          Ende nuk ke dokumente. Mund të ngarkosh analiza, epikriza dhe receta
          nga butoni “Ngarko”.
        </p>
      ) : (
        <div className="p2-myhealth-docs-list">
          {documents.map((doc) => {
            const typeLabel = fileTypeLabel(doc);
            const ext = fileExtension(doc);
            const sizeLabel = humanFileSize(doc.fileSize);
            const sourceLabel =
              doc.sourceLabel ||
              doc.labName ||
              doc.clinicName ||
              doc.doctorName ||
              "Ngarkuar nga ti";

            // Optional: appointment / record link info if you have it
            const linkedLabel =
              doc.linkedAppointmentLabel ||
              doc.linkedDoctorName ||
              doc.linkedVisitLabel ||
              "";

            return (
              <button
                key={doc.id}
                type="button"
                onClick={() => onOpenDocument?.(doc)}
                className="p2-myhealth-docs-item"
              >
                {/* Left icon */}
                <div className="p2-myhealth-docs-icon">
                  <FileText className="p2-myhealth-docs-icon-svg" />
                </div>

                {/* Main content */}
                <div className="p2-myhealth-docs-main">
                  {/* Title */}
                  <p className="p2-myhealth-docs-name">
                    {doc.title || doc.fileName || "Dokument pa titull"}
                  </p>

                  {/* Type + source */}
                  <p className="p2-myhealth-docs-subtitle">
                    {typeLabel}
                    {sourceLabel && (
                      <>
                        {" "}
                        • <span className="p2-myhealth-docs-source">
                          {sourceLabel}
                        </span>
                      </>
                    )}
                  </p>

                  {/* Linked visit / doctor (optional) */}
                  {linkedLabel && (
                    <p className="p2-myhealth-docs-linked">
                      I lidhur me: {linkedLabel}
                    </p>
                  )}

                  {/* Meta row: ext • size • date */}
                  <div className="p2-myhealth-docs-meta">
                    {ext && (
                      <span className="p2-myhealth-docs-pill">
                        {ext}
                      </span>
                    )}
                    {sizeLabel && (
                      <span className="p2-myhealth-docs-pill">
                        {sizeLabel}
                      </span>
                    )}
                    {doc.dateLabel && (
                      <span className="p2-myhealth-docs-date">
                        {doc.dateLabel}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
